import { c as _c } from "react-compiler-runtime";
import { useCallback, useState } from 'react';
import { COLUMN, ROW } from '../types/observerTypes';
import { useUpdateVisibleIndexes } from '../VirtualizationContext';
import { DEFAULT_COLUMN_WIDTH, TABLE_ROW_HEIGHT } from '../../table/constants/sizes';

//from MDN
//https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API
//```
//Be aware that your callback is executed on the main thread.
//It should operate as quickly as possible;
//if anything time-consuming needs to be done, use Window.requestIdleCallback().
//```
const handleIntersectionFactory = updateVisibleIndexes => {
  const handleIntersection = entries => {
    const visCols = new Set();
    const visRows = new Set();
    let hasUpdate = false;
    for (const {
      target,
      isIntersecting
    } of entries) {
      const type = target.getAttribute('data-observer-type');
      const id = target.getAttribute('data-observer-id');

      // Skip elements without a type or id. This should never actually happen,
      // so long as we're setting the `data-observer-*` attributes properly.
      if (!id || !type) {
        continue;
      }
      if (type === COLUMN && isIntersecting) {
        visCols.add(Number(id));
        hasUpdate = true;
      } else if (type === ROW && isIntersecting) {
        visRows.add(Number(id));
        hasUpdate = true;
      }
    }
    if (hasUpdate) {
      updateVisibleIndexes({
        rows: visRows,
        columns: visCols
      });
    }
  };
  return handleIntersection;
};
export const useObserveTableContainerRef = () => {
  const $ = _c(5);
  const [observer, setObserver] = useState(null);
  const updateVisibleIndexes = useUpdateVisibleIndexes();
  let t0;
  if ($[0] !== updateVisibleIndexes) {
    t0 = scrollElementRef => {
      setObserver(currentObserver => {
        if (currentObserver) {
          currentObserver.disconnect();
        }
        if (!scrollElementRef) {
          return null;
        }
        const handleIntersection = handleIntersectionFactory(updateVisibleIndexes);
        return new IntersectionObserver(handleIntersection, {
          root: scrollElementRef,
          rootMargin: `${TABLE_ROW_HEIGHT * 5}px ${DEFAULT_COLUMN_WIDTH * 5}px`
        });
      });
    };
    $[0] = updateVisibleIndexes;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const handleObserveRef = t0;
  let t1;
  if ($[2] !== handleObserveRef || $[3] !== observer) {
    t1 = {
      handleObserveRef,
      observer
    };
    $[2] = handleObserveRef;
    $[3] = observer;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
};